import { Controller } from "@hotwired/stimulus"

// Disables allowance rules that cannot be combined, and says which rule blocks
// them.
//
// The model refuses these combinations at save time (INCOMPATIBLE_RATE_TIER_RULES
// and friends). Without this the admin fills in a Staffelung, saves, and only
// then learns it cannot be used together with the Stundenmaximum they set three
// fields earlier. The rule pairs come from the model via a data value, so there
// is one source of truth for what collides.
//
// A rule that is already ON is never disabled — otherwise the admin could not
// switch it off again to resolve the conflict.
export default class extends Controller {
  static targets = ["rule"]
  // note/conjunction come from de.yml — no German is spelled out in here.
  static values = { rules: Object, labels: Object, note: String, conjunction: String }

  connect() {
    this.refresh()
  }

  refresh() {
    const active = this.activeRules()

    this.ruleTargets.forEach((wrapper) => {
      const rule = wrapper.dataset.compatRule
      const blockers = (this.rulesValue[rule] || []).filter((other) => active.includes(other))
      const selfActive = active.includes(rule)

      this.setDisabled(wrapper, blockers.length > 0 && !selfActive)
      this.setNote(wrapper, selfActive ? [] : blockers)
    })
  }

  // A rule counts as active when its section is offered for the current type and
  // its control carries a value: ticked for a switch, filled in for a number.
  activeRules() {
    return this.ruleTargets
      .filter((wrapper) => this.ruleActive(wrapper))
      .map((w) => w.dataset.compatRule)
  }

  ruleActive(wrapper) {
    if (this.sectionHidden(wrapper)) return false

    const input = this.inputFor(wrapper)
    if (!input) return false
    if (input.type === "checkbox") return input.checked
    return input.value.trim() !== ""
  }

  inputFor(wrapper) {
    return wrapper.querySelector('input[type="checkbox"], input[type="number"]')
  }

  // A rule whose section is hidden for the selected allowance type is not in
  // play, whatever value its input still carries — the save drops it too. The
  // walk stops at the editor root, so a collapsed card (root hidden) and the
  // three inactive tab panels do not count as hiding anything.
  //
  // It starts at the wrapper itself, not at its parent: for max_hours the rule
  // wrapper IS the type-gated section, so skipping it left a stale Stundenmaximum
  // blocking rules the save would have accepted.
  sectionHidden(wrapper) {
    let el = wrapper
    while (el && el !== this.element) {
      const isPanel = el.dataset.allowanceEditorTabsTarget === "panel"
      if (!isPanel && el.classList.contains("hidden")) return true
      el = el.parentElement
    }
    return false
  }

  setDisabled(wrapper, disabled) {
    const input = this.inputFor(wrapper)
    if (!input) return
    input.disabled = disabled
    wrapper.classList.toggle("opacity-60", disabled)
  }

  setNote(wrapper, blockers) {
    const note = wrapper.querySelector("[data-compat-note]")
    if (!note) return

    if (blockers.length === 0) {
      note.textContent = ""
      note.classList.add("hidden")
      return
    }

    const names = blockers.map((rule) => this.labelsValue[rule] || rule)
    note.textContent = this.noteValue.replace("%{rules}", this.toSentence(names))
    note.classList.remove("hidden")
  }

  toSentence(names) {
    if (names.length <= 1) return names.join("")
    return `${names.slice(0, -1).join(", ")} ${this.conjunctionValue} ${names[names.length - 1]}`
  }
}
